import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import CustomButton from "../../../components/Button/CustomButton";
import type { Dispatch } from "../types";

export type DispatchValidationMode = "approve" | "reject";

/** Payload emitted by the dialog once the user confirms. */
export type DispatchValidationSubmit =
  | { mode: "approve"; invoice_number: string; observation?: string }
  | { mode: "reject"; observation: string };

export interface DispatchValidationDialogProps {
  open: boolean;
  mode: DispatchValidationMode;
  dispatch: Dispatch | null;
  onClose: () => void;
  onConfirm: (result: DispatchValidationSubmit) => Promise<void>;
}

/**
 * Approve/reject dialog for a dispatch. Both modes require an observation
 * (S2 — the contador records its reasoning on approve and on reject); the
 * approve mode additionally requires the invoice number. `onConfirm` may
 * reject: the dialog stays open so the page handler can surface the error,
 * and closes on success.
 */
export function DispatchValidationDialog({
  open,
  mode,
  dispatch,
  onClose,
  onConfirm,
}: DispatchValidationDialogProps) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [observation, setObservation] = useState("");
  const [invoiceTouched, setInvoiceTouched] = useState(false);
  const [observationTouched, setObservationTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setInvoiceNumber("");
      setObservation("");
      setInvoiceTouched(false);
      setObservationTouched(false);
      setIsSubmitting(false);
    }
  }, [open, mode, dispatch?.id]);

  const isApprove = mode === "approve";
  const invoiceEmpty = invoiceNumber.trim().length === 0;
  const observationEmpty = observation.trim().length === 0;
  // S2: the observation is mandatory in both modes, so approve needs it too
  // (not only the invoice).
  const submitDisabled =
    isSubmitting || (isApprove ? invoiceEmpty || observationEmpty : observationEmpty);

  const quote =
    dispatch?.quote_number || dispatch?.reservation?.quote_number || null;

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setInvoiceTouched(true);
    setObservationTouched(true);
    setIsSubmitting(true);
    try {
      if (isApprove) {
        await onConfirm({
          mode: "approve",
          invoice_number: invoiceNumber.trim(),
          observation: observation.trim(),
        });
      } else {
        await onConfirm({ mode: "reject", observation: observation.trim() });
      }
      onClose();
    } catch {
      // The page already shows an error toast; keep the dialog open to retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isApprove ? "Aprobar despacho" : "Rechazar despacho"}
        {dispatch ? ` #${dispatch.id}` : ""}
        {quote ? ` — ${quote}` : ""}
      </DialogTitle>

      <DialogContent>
        {isApprove && (
          <TextField
            autoFocus
            label="Factura"
            variant="outlined"
            fullWidth
            margin="normal"
            value={invoiceNumber}
            onChange={(e) => {
              setInvoiceNumber(e.target.value);
              setInvoiceTouched(true);
            }}
            onBlur={() => setInvoiceTouched(true)}
            inputProps={{ maxLength: 100, "aria-label": "invoice-number" }}
            error={invoiceTouched && invoiceEmpty}
            helperText={
              invoiceTouched && invoiceEmpty
                ? "La factura es obligatoria"
                : undefined
            }
          />
        )}

        <TextField
          label="Observaciones"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          margin="normal"
          value={observation}
          onChange={(e) => {
            setObservation(e.target.value);
            setObservationTouched(true);
          }}
          onBlur={() => setObservationTouched(true)}
          inputProps={{ maxLength: 500, "aria-label": "dispatch-observation" }}
          error={observationTouched && observationEmpty}
          helperText={
            observationTouched && observationEmpty
              ? "La observación es obligatoria"
              : undefined
          }
        />
      </DialogContent>

      <DialogActions>
        <CustomButton
          onClick={onClose}
          color="outlined"
          disabled={isSubmitting}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={handleSubmit}
          color={isApprove ? "primary" : "secondary"}
          disabled={submitDisabled}
          isLoading={isSubmitting}
          icon={isApprove ? CheckCircleIcon : CancelIcon}
        >
          {isApprove ? "Aprobar" : "Rechazar"}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}

export default DispatchValidationDialog;
